import React, { useEffect, useRef, useState } from 'react'

import { FieldData } from 'rc-field-form/lib/interface'
import { useNavigate } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import styled, { css, CSS } from 'styled-components'

import { ModalsPlugin, GlobalModalTypeEnum } from '@api/local'
import { Button, Heading, Paragraph, Spacer, StaticImage } from '@atoms/index'
import { ResponsivePXValue } from '@components/Theme'
import { useConfig } from '@contexts/ConfigProvider'
import {
  CheckOutFlowTypeEnum,
  CheckoutStepsEnum, NumberOfPortionsEnum, PlansPageQuery, useGetAllMealKitDefaultPricingQuery,
  useGetAllMealKitSubscriptionFees, usePlansPageQuery,
} from '@hooks/api'
import { reportCheckoutUiStatusGap } from '@lib/checkoutObservability'
import { Form, RadioButton, RadioButtonProps } from '@molecules/index'
import {
  AddressForm, CreditCardForm, PlansSectionContainer, SignUpSuccessModal,
  UserAddressList,
  UserCardList,
} from '@organisms/index'
import {
  getPresetPrice, shouldDisplayConfirmation, getHydratedPlan,
  getPeopleRadioProps, getDishesRadioProps, getCategoryRadioProps,
  isConfirmDetailsButtonDisabled, shouldHideSubscriptionStep, shouldForceChooseAmountStep, isPlanChangedUtil,
  getIsPlansNextButtonDisabled, shouldBlockOnDemandGuestStep, iconDetails,
  CheckoutFlowState, StepConfig,
  CheckoutStepRendererProps,
  getNextStepForHandler,
  PerDishPricingItem,
  getSafeShouldPresentCheckoutUI,
  getSafeHasActiveSubscription,
} from '@utility/CheckoutPure'
import * as checkoutStorage from '@utility/CheckoutStorage'
import { checkoutLogger } from '@utility/Logger'

import { CheckoutDeliveryDaySelect } from './CheckoutDeliveryDaySelect'

const IconFlexRow = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  width: 100%;

  ${ResponsivePXValue('gap', { mobile: '24px', tablet: '32px', desktop: '48px' })}

  ${(props): CSS => {
    if (props?.$isMobile) {
      return css`
        flex-direction: column;
        align-items: center;
      `
    }
  }}
`

const IconColumn = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  ${ResponsivePXValue('min-width', '180px')}
  ${ResponsivePXValue('max-width', '260px')}
`

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  ${ResponsivePXValue('width', '96px')}
  ${ResponsivePXValue('height', '96px')}
`

const SectionRow = styled.div`
  display: flex;
  justify-content: space-between;
  ${ResponsivePXValue('width', { mobile: '100%' })}
`

const SectionColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  `

const SectionColumnInner = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  ${ResponsivePXValue('width', { mobile: '100%' })}
`

// Pure: Get category radio options
export const getCategoryOptions = (
  plansData: PlansPageQuery,
  isMobile: boolean,
  state: { numberOfPortions?: NumberOfPortionsEnum | null, numberOfMeals?: number },
  perDishPricingDataArray: PerDishPricingItem[] | undefined,
  fixedFee: number | undefined,
): RadioButtonProps['options'] => {
  return (
    plansData?.categories?.list?.filter((category) => category.canSubscribe).map((plan) => ({
      value: plan.id,
      element: <>
        <Heading variant='h6'>{plan.title}</Heading>
        <Paragraph variant={isMobile ? 'p3' : 'p1'} className='plans-text plans-desc'>{plan.description}</Paragraph>
        {state.numberOfPortions != null && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Heading variant='h2' className='plans-text price'>
              {getPresetPrice(state.numberOfMeals ?? 2, state.numberOfPortions, plan.id, perDishPricingDataArray, fixedFee)}
            </Heading>
            <Paragraph variant='p4' className='plans-text'>/ week</Paragraph>
          </div>
        )}
      </>,
    })) ?? []
  )
}

export function CheckoutStepRenderer(props: CheckoutStepRendererProps & { currentSteps: StepConfig[] }) {
  const {
    currentStepObj,
    state,
    form,
    isMobile,
    setState,
    handleOnCardClose,
    handleChange,
    handleConfirmDetails,
    handleUpdateSubscription,
    actionTitle,
    currentSteps,
    // checkoutStatus,
  } = props

  const [isPlanChanged, setIsPlanChanged] = useState(false)
  const navigate = useNavigate()
  // --- Prevent ON_DEMAND guest modal/toast infinite loop ---
  const hasShownGuestBlockRef = useRef(false)
  const originalPlanRef = useRef({
    numberOfPortions: state.numberOfPortions,
    numberOfMeals: state.numberOfMeals,
    categoryId: state.categoryId,
  })
  // Ref to track last hydrated plan from API/subscription
  const lastHydratedPlanRef = useRef({
    numberOfPortions: props.subscription?.numberOfPortions ?? null,
    numberOfMeals: props.subscription?.numberOfMeals ?? null,
    categoryId: props.subscription?.category?.id ?? null,
  })

  const { data: plansData } = usePlansPageQuery()
  const { data: perDishPricingData } = useGetAllMealKitDefaultPricingQuery({ variables: { limit: 40 } })
  const { data: fixedFee } = useGetAllMealKitSubscriptionFees({ variables: { limit: 1 } })
  const { addToast } = useToasts()
  const config = useConfig()

  const hasUserEnteredAddressFromModal = config.isBrowser() && checkoutStorage.getCheckoutSession('hasUserEnteredAddressFromModal') === 'true'
  const displayConfirmation = shouldDisplayConfirmation(hasUserEnteredAddressFromModal, state.checkoutFlowType)
  const perDishPricingDataArray = perDishPricingData?.allDefaultMealKitPricing?.list
  const fixedFeeValue = fixedFee?.subscriptionFee?.list[0]?.price
  const isIOS = useRef<boolean>(typeof navigator !== 'undefined' ? /iPad|iPhone|iPod/.test(navigator.userAgent) : false)
  const navigateForModal = isIOS.current && state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION

  // Pure function results as well-named variables
  const hydratedPlan = getHydratedPlan(props.subscription)
  // Removed unused addressNextStepIndex and paymentNextStepIndex; canonical logic is now used in handlers
  const peopleRadioProps = getPeopleRadioProps()
  const dishesRadioProps = getDishesRadioProps(addToast)
  const categoryOptions = getCategoryOptions(
    plansData,
    isMobile,
    state,
    perDishPricingDataArray,
    fixedFeeValue,
  )
  const categoryRadioProps = getCategoryRadioProps(categoryOptions, state)
  const isConfirmDetailsDisabled = isConfirmDetailsButtonDisabled(
    state.pageLoading,
    state.checkoutFlowType,
    state.selectedDeliveryDay,
  )
  const shouldHideStep = shouldHideSubscriptionStep(
    state.checkoutFlowType,
    getSafeShouldPresentCheckoutUI(props.checkoutStatus),
    props.currentStepObj.id,
  )
  const shouldForceChooseAmount = shouldForceChooseAmountStep(
    state.checkoutFlowType,
    getSafeShouldPresentCheckoutUI(props.checkoutStatus),
    state.currentStep,
    state.maxCompleted,
  )

  // Guarded step advancement helper
  const onAdvanceStep = (stepIndex: number) => {
    const result = props.handleGoToStepGuarded ? props.handleGoToStepGuarded(stepIndex) : { allowed: true }
    if (!result.allowed && result.reason === 'GUEST_ON_DEMAND_BLOCK') {
      addToast('Please sign up or log in to continue to the next step.', {
        appearance: 'warning',
        autoDismiss: true,
      })
      _handleSignUp()
      return false
    }
    return true
  }

  // iconDetails now imported from pure section above

  const _handleSignUp = (): void => {
    ModalsPlugin.shared().toggleGlobalModal(true, GlobalModalTypeEnum.SIGN_UP)
  }

  const _handleSuccessModal = async (): Promise<void> => {

    const successModalData = await handleConfirmDetails()
    if (navigateForModal) {
      navigate('/meal-kit', { state: { isFromCheckoutFlow: true, orderData: successModalData } })
    } else {
      setState((prev) => ({ ...prev, showSuccessModal: true }))

      if (state.checkoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND) {
        if (successModalData !== undefined) {
          setState((prev: CheckoutFlowState) => ({
            ...prev,
            orderData: successModalData,
          }))
        }
      }
    }
  }

  const handleDeliveryDaySelect = (selectedDay: string) => {
    setState((prev: CheckoutFlowState) => ({ ...prev, selectedDeliveryDay: selectedDay }))
  }

  const _handleCloseSuccessModal = (): void => {
    // No-op: removed console log for production
    setState((prev) => ({ ...prev, showSuccessModal: false }))
  }
  // Listen for user changes and compare to original values
  const handlePlanFieldChange = (changedFields: FieldData[]) => {
    const people = form.getFieldValue('peopleRadio')
    const dishes = form.getFieldValue('dishesRadio')
    const category = form.getFieldValue('categoryRadio')
    const orig = originalPlanRef.current
    const result = isPlanChangedUtil(orig, people, dishes, category)
    setIsPlanChanged(result)
    handleChange(changedFields)
  }

  // Modified planNextClickHandler for shouldPresentCheckoutUI === false
  const planNextClickHandler = async (): Promise<void> => {
    if (props.checkoutStatus && props.checkoutStatus.shouldPresentCheckoutUI === false) {
      if (props.checkoutStatus.hasActiveSubscription) {
        if (isPlanChanged) {
          await handleUpdateSubscription()
          addToast('Subscription updated!', { appearance: 'success', autoDismiss: true })
          setIsPlanChanged(false)
        } else {
          addToast('No changes to update.', { appearance: 'info', autoDismiss: true })
        }
      } else if (props.checkoutStatus.hasPausedSubscription) {
        await handleUpdateSubscription()
        addToast('Subscription activated!', { appearance: 'success', autoDismiss: true })
        setIsPlanChanged(false)
      }
      // Never advance step
      return
    }

    if (!onAdvanceStep(state.currentStep + 1)) return

    if (state.checkoutFlowType === 'SUBSCRIPTION') {
      const people = form.getFieldValue('peopleRadio')
      const dishes = form.getFieldValue('dishesRadio')
      const category = form.getFieldValue('categoryRadio')
      if (people && dishes && category) {
        await handleUpdateSubscription()
        setState((prevState) => ({ ...prevState, maxCompleted: 2, currentStep: 2 }))
      } else {
        addToast('Please complete all selections before continuing.', {
          appearance: 'warning',
          autoDismiss: true,
        })
      }
    }
  }

  // useEffect(() => {
  //   if (state.orderData && !state.showSuccessModal) {
  //     setState((prev) => ({ ...prev, showSuccessModal: true }))
  //   }
  // }, [state.orderData])

  // Hydrate form and originalPlanRef from subscription only when plan changes
  useEffect(() => {
    if (props.checkoutStatus && props.checkoutStatus.shouldPresentCheckoutUI === false && props.subscription) {
      const lastHydrated = lastHydratedPlanRef.current
      const hasHydratedChanged =
        hydratedPlan.numberOfPortions !== lastHydrated.numberOfPortions ||
        hydratedPlan.numberOfMeals !== lastHydrated.numberOfMeals ||
        hydratedPlan.categoryId !== lastHydrated.categoryId
      if (hasHydratedChanged) {
        form.setFieldsValue({
          peopleRadio: hydratedPlan.numberOfPortions,
          dishesRadio: hydratedPlan.numberOfMeals,
          categoryRadio: hydratedPlan.categoryId,
        })
        originalPlanRef.current = { ...hydratedPlan }
        lastHydratedPlanRef.current = { ...hydratedPlan }
        setIsPlanChanged(false)
      }
    }
  }, [props.checkoutStatus?.shouldPresentCheckoutUI, props.subscription?.numberOfPortions, props.subscription?.numberOfMeals, props.subscription?.category?.id, form, hydratedPlan])

  // --- PATCH: Prevent step advancement if shouldPresentCheckoutUI is false ---
  // Always force step 0 (CHOOSE_AMOUNT) if shouldPresentCheckoutUI is false, but ONLY for SUBSCRIPTION
  useEffect(() => {
    if (shouldForceChooseAmount) {
      // Prevent infinite loop: only set if not already at 0,0
      setState((prev) => {
        if (prev.currentStep === 0 && prev.maxCompleted === -1) return prev
        const newState = { ...prev, currentStep: 0, maxCompleted: -1 }
        return newState
      })
    }
  }, [shouldForceChooseAmount, state.currentStep, state.maxCompleted, setState])

  // --- FIX: Move conditional return after all hooks to avoid hook order violation ---
  if (shouldHideStep) {
    return null
  }

  const isPlansNextButtonDisabled = getIsPlansNextButtonDisabled(
    getSafeShouldPresentCheckoutUI(props.checkoutStatus),
    getSafeHasActiveSubscription(props.checkoutStatus),
    isPlanChanged,
    state.pageLoading,
    form,
  )

  if (!currentStepObj) {
    checkoutLogger.error('[BUG] currentStepObj is undefined', { props, state })
    return <Paragraph variant='p1' align='center'> Invalid step </Paragraph>
  }

  // Canonical step logic for handler: use the same pure function as auto-advance for both flows
  const addressSuccessHandler = () => {
    if (props.checkoutStatus && props.checkoutStatus.shouldPresentCheckoutUI === false) return
    const nextStep = getNextStepForHandler({
      state: state as CheckoutFlowState,
      currentSteps,
      checkoutStatus: props.checkoutStatus,
      checkoutLogger,
    })
    checkoutLogger.info('[CHECKOUT][addressSuccessHandler][STEP_TRANSITION]', {
      from: state.currentStep,
      to: nextStep,
    })
    if (typeof nextStep === 'number' && nextStep !== state.currentStep) {
      setState((prevState) => ({ ...prevState, maxCompleted: Math.max(prevState.maxCompleted, nextStep), currentStep: nextStep }))
    }
  }

  // Canonical step logic for payment handler as well
  const creditCardNextClickHandler = () => {
    if (props.checkoutStatus && props.checkoutStatus.shouldPresentCheckoutUI === false) return
    const nextStep = getNextStepForHandler({
      state: state as CheckoutFlowState,
      currentSteps,
      checkoutStatus: props.checkoutStatus,
      checkoutLogger,
    })
    checkoutLogger.info('[CHECKOUT][creditCardNextClickHandler][STEP_TRANSITION]', {
      from: state.currentStep,
      to: nextStep,
    })
    if (typeof nextStep === 'number' && nextStep !== state.currentStep) {
      setState((prevState) => ({ ...prevState, maxCompleted: Math.max(prevState.maxCompleted, nextStep), currentStep: nextStep }))
    }
  }

  // --- Stepper click handler: block ON_DEMAND guest step navigation ---
  // This should be passed to CheckoutStepper via onStepSelect, but if not, add a useEffect to watch state.currentStep and block if needed
  useEffect(() => {
    if (!props.checkoutStatus?.id) {
      reportCheckoutUiStatusGap({
        surface: 'CheckoutStepRenderer',
        reason: 'renderer_effect_without_status',
        stateFlowType: state.checkoutFlowType,
        hasCheckoutStatusId: false,
      })
      return
    }
    const shouldBlockGuest = shouldBlockOnDemandGuestStep(
      props.checkoutStatus,
      state.currentStep,
      hasShownGuestBlockRef.current,
      props.handleGoToStepGuarded,
    )
    if (shouldBlockGuest) {
      // If user is a guest and tries to go to address step, block and show modal
      const addressStepIdx = 0 // In ON_DEMAND, first step is address
      const result = props.handleGoToStepGuarded!(addressStepIdx)
      if (!result.allowed && result.reason === 'GUEST_ON_DEMAND_BLOCK') {

        checkoutLogger.info('[GUARD_BLOCK][ON_DEMAND_GUEST] Triggering sign up modal')

        addToast('Please sign up or log in to continue to the next step.', {
          appearance: 'warning',
          autoDismiss: true,
        })
        _handleSignUp()
        hasShownGuestBlockRef.current = true
      }
    }
  }, [state.currentStep, props.checkoutStatus?.checkoutFlowType, props.checkoutStatus?.hasAccount, props.handleGoToStepGuarded])

  // Reset the guest block ref if user advances steps or logs in
  useEffect(() => {
    if (!props.checkoutStatus?.id) {
      return
    }
    if (state.currentStep !== 0 || props.checkoutStatus.hasAccount) {
      hasShownGuestBlockRef.current = false
    }
  }, [state.currentStep, props.checkoutStatus?.hasAccount, props.checkoutStatus?.id])

  return (
    <>
      <If condition={state.showSuccessModal}>
        <SignUpSuccessModal
          orderData={state.orderData}
          isOnDemandOrder={state.checkoutFlowType === 'ON_DEMAND'}
          open={state.showSuccessModal}
          onClose={_handleCloseSuccessModal}
          checkoutFlowType={state.checkoutFlowType}
        />
      </If>

      <Choose>
        <When condition={
          currentStepObj.id === CheckoutStepsEnum.CHOOSE_AMOUNT ||
        currentStepObj.id === CheckoutStepsEnum.PICK_PLAN
        }>
          <>
            <PlansSectionContainer heading='Get everything you need for dinner with no locked-in plan' invertStyle={isMobile}>
              <IconFlexRow $isMobile={isMobile}>
                {iconDetails.map(iconDetail => (
                  <IconColumn $isMobile={isMobile} key={iconDetail.staticId}>
                    <IconCircle>
                      <StaticImage
                        staticId={iconDetail.staticId}
                        lazy={false}
                        objectFit='contain'
                        mobileWidth={430}
                        tabletWidth={430}
                        desktopWidth={768} />
                    </IconCircle>
                    <Heading variant='h5' align='center'>{iconDetail.title}</Heading>
                    <Paragraph variant='p1' align='center'>{iconDetail.description}</Paragraph>
                  </IconColumn>
                ))}
              </IconFlexRow>
              <Spacer universal='24px'/>
            </PlansSectionContainer>
            <Spacer universal='24px'/>
            <Form form={form} onFieldsChange={handlePlanFieldChange}>
              <PlansSectionContainer heading='How many servings and how often?'>
                <SectionColumn>
                  <Spacer universal='24px' />
                  <SectionRow>
                    <SectionColumnInner>
                      <Heading variant='h5' className='people-radio-title' align='center'>
                        How many people are you cooking for?
                      </Heading>
                      <RadioButton {...peopleRadioProps} />
                    </SectionColumnInner>
                  </SectionRow>
                  <Spacer universal='24px' />
                  <SectionRow>
                    <SectionColumnInner>
                      <Heading variant='h5' className='people-radio-title' align='center'>
                        How many dishes would you like per week?
                      </Heading>
                      <RadioButton {...dishesRadioProps} />
                    </SectionColumnInner>
                  </SectionRow>
                  <Spacer universal='24px' />
                </SectionColumn>
              </PlansSectionContainer>
              <Spacer universal='24px'/>
              <PlansSectionContainer heading='Pick a Plan' subHeading='You can pick meals weekly.
Too busy? This plan is your built-in backup.'>
                <Spacer universal='24px' />
                <SectionRow>
                  <RadioButton {...categoryRadioProps} className='radio-button-wrapper category-radio' />
                </SectionRow>
              </PlansSectionContainer>
              <Spacer universal='24px' />
              <Button
                className='next-button'
                title={actionTitle}
                color='black'
                disabled={isPlansNextButtonDisabled}
                fullWidth={isMobile}
                onClick={planNextClickHandler}
                loading={state.pageLoading}/>
              <Spacer universal='24px' />
            </Form>
            <Spacer universal='24px'/>
          </>
        </When>
        <When condition={currentStepObj.id === CheckoutStepsEnum.ADD_ADDRESS}>
          <Spacer desktop='24px' />
          <PlansSectionContainer heading='Add Address'>
            <SectionColumn>
              <Spacer desktop='24px' />
              <AddressForm className='checkout-section'
                displayActionButtons={true}
                disabled={false}
                onSuccess={addressSuccessHandler}
                actionButtonText='NEXT'
                checkoutFlowType = {state.checkoutFlowType}
              />
              <Spacer universal='24px' />
            </SectionColumn>
          </PlansSectionContainer>
          <Spacer universal='24px' />
        </When>
        <When condition={currentStepObj.id === CheckoutStepsEnum.ADD_PAYMENT}>
          <>
            <Spacer universal='24px' />
            <PlansSectionContainer
              heading='Add Card'
              subHeading={state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION ? 'You will not be charged today.' : undefined}
            >
              <Spacer universal='24px' />
              <CreditCardForm
                actionButtonText='NEXT'
                onClose={handleOnCardClose}
                onCardSuccess={creditCardNextClickHandler}
                secondaryActionButtonText='RESET'
                checkoutFlowType={state.checkoutFlowType}
              />
              <Spacer universal='24px' />
            </PlansSectionContainer>
          </>
        </When>
        <When condition={currentStepObj.id === CheckoutStepsEnum.CONFIRM_DETAILS}>
          <Spacer universal='24px' />
          <PlansSectionContainer heading='Almost there!' subHeading='Please confirm details below'>
            <FlexColumn>
              <Spacer universal='16px' />
              <Heading variant='h6'>Delivery Address</Heading>
              <Spacer universal='16px' />
              <UserAddressList displayConfirmation={displayConfirmation} />
              <Spacer universal='24px' />
              <Heading variant='h6'>Payment Details</Heading>
              <Spacer universal='16px' />
              <UserCardList />
              <Spacer universal='16px' />
              <CheckoutDeliveryDaySelect
                checkoutFlowType={state.checkoutFlowType}
                onDeliverDaySelect={handleDeliveryDaySelect}
              />
              <Spacer universal='16px' />
              <Button
                className='next-button'
                title={actionTitle}
                color='black'
                disabled={isConfirmDetailsDisabled}
                fullWidth={isMobile}
                onClick={_handleSuccessModal}
                loading={state.pageLoading}/>
            </FlexColumn>
          </PlansSectionContainer>
        </When>
        <When condition={currentStepObj.id === CheckoutStepsEnum.SELECT_MEAL_KITS}>
          <Spacer universal='24px' />
          <PlansSectionContainer heading='Select Meal Kits'>
            <Spacer universal='24px' />
          </PlansSectionContainer>
        </When>
        <Otherwise>
          {(() => {
            checkoutLogger.error('Invalid or unrecognized step', { currentStepObj })
            return <Paragraph variant='p1' align='center'> Invalid step </Paragraph>
          })()}
        </Otherwise>
      </Choose>
    </>
  )
}
